import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, ViewChild, inject, signal } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Observable, finalize, take, timer } from 'rxjs';
import { AuthService } from 'src/app/pages/auth/services/auth.service';
import { DisbursementRoundService } from 'src/app/services/api/disbursement/disbursement-round.service';
import { ToastService } from 'src/app/services/toast-service/toast.service';
import {
  DisbursementRoundListParams,
  DisbursementRoundModel,
  DisbursementRoundStatusCode,
  ROUND_DOC_POLL_INTERVAL_MS,
  roundArtifactsPending,
} from 'src/app/services/models/disbursement/disbursement-round.model';
import {
  DisbursementStatusAudience,
  DisbursementStatusDisplay,
  disbursementStatusAudienceFor,
  getDisbursementRoundStatusDisplay,
} from 'src/app/services/models/disbursement/disbursement-status-display';
import { DisbursementApprovalTrackEntry, DisbursementPath } from 'src/app/services/models/disbursement/disbursement.model';
import { ConfirmDialogComponent } from 'src/shared/confirm-dialog/confirm-dialog.component';
import { SharedModule } from 'src/shared/shared.module';
import { toBuddhistYear } from 'src/utils/academic-term-options';
import { extractBlobErrorMessage } from 'src/utils/blob-error';
import { RoleID } from 'src/utils/enum/role.enum';
import {
  DisbursementRevisionDialogComponent,
  DisbursementRevisionDialogResult,
} from '../_shared/disbursement-revision-dialog/disbursement-revision-dialog.component';

const PREVIEW_ERROR_FALLBACK = 'ยังไม่มีเอกสารรวมของรอบนี้';

const ROUND_PATH_LABELS: Record<DisbursementPath, string> = {
  regular: 'หลักสูตรปกติ',
  international: 'หลักสูตรนานาชาติ',
};

// v2.0 phase 2 — the three signing stations. Each role only ever has one action, so the
// kind is resolved from (active role, round status) and never from a button the user picked.
type RoundActionKind = 'finance_sign' | 'head_certify' | 'vice_dean_sign';

// Station verbs (เซ็นต์เอกสารส่งเบิก/เห็นชอบ/อนุมัติ, head-first) — context/reference/GLOSSARY_Disbursement.md §3
const ROUND_ACTION_VERB: Record<RoundActionKind, string> = {
  head_certify: 'เซ็นต์เอกสารส่งเบิก',
  finance_sign: 'เห็นชอบ',
  vice_dean_sign: 'อนุมัติ',
};

// head_certify's verb (เซ็นต์เอกสารส่งเบิก) reads as a complete action on its own — the other two stations are
// bare verbs and still need "เอกสาร"/"เอกสารส่งเบิก" appended when composed below.
const ROUND_ACTION_VERB_STANDS_ALONE: Partial<Record<RoundActionKind, true>> = {
  head_certify: true,
};

type RoundQueueTab = 'all' | 'pending';

// R6 — the status each signing role is actually queued at. The default tab narrows the
// list to it so an approver never reads rounds parked at somebody else's station; the
// ทั้งหมด tab widens back to whatever DisbursementRoundPolicy already lets the role see
// (การเงิน deliberately keeps sight of the rounds it has acted on).
const ROUND_PENDING_STATUS_BY_ROLE: Partial<Record<RoleID, DisbursementRoundStatusCode>> = {
  [RoleID.FINANCE_OFFICER]: 'pending_finance_sign',
  [RoleID.HEAD_OF_DEPARTMENT]: 'pending_head',
  [RoleID.VICE_DEAN_ADMINISTRATION]: 'pending_vice_dean_admin',
};

interface PreviewPanelState {
  loading: boolean;
  error: boolean;
  errorMessage: string | null;
  url: SafeResourceUrl | null;
}

@Component({
  selector: 'app-disbursement-round-queue',
  imports: [
    CommonModule,
    SharedModule,
    DisbursementRevisionDialogComponent,
  ],
  templateUrl: './round-queue.component.html',
  styleUrl: './round-queue.component.scss',
})
export class DisbursementRoundQueueComponent implements OnDestroy {
  @ViewChild('roundConfirmDialog')
  roundConfirmDialog!: ConfirmDialogComponent;
  @ViewChild('actionSuccessDialog')
  actionSuccessDialog?: ConfirmDialogComponent;
  @ViewChild('sendBackDialog')
  sendBackDialog!: DisbursementRevisionDialogComponent;

  // null when idle — holds the teardown of the active ADR-CE poll interval. Kept as a
  // closure because an rxjs `Subscription` type annotation trips this repo's lint env
  // (rxjs type drift, same root cause that breaks ng build locally). Declared above the
  // @Input setter — the file's member-ordering rule flags anything declared after it.
  private stopRoundDocPoll: (() => void) | null = null;

  // หน้าที่ฝังคอมโพเนนต์นี้ไว้ใต้แท็บชื่อ "รอบส่งเบิก" อยู่แล้วปิดหัวข้อซ้ำได้ — ตัวนับ
  // "ทั้งหมด N รอบ" ปิดไปพร้อมกัน (86eyp4arx: ทั้งหัวข้อและตัวนับถูกกากบาททิ้งทั้งคู่)
  @Input() showHeading = true;

  @Input()
  set academicTermId(value: number | null | undefined) {
    const next = value ?? null;
    if (next === this.termId) {
      return;
    }

    this.termId = next;
    this.loadRounds(1);
  }

  // 86eypuk2w — ช่องค้นหาของหน้าแม่ (ตรวจสอบและอนุมัติเอกสารส่งเบิก) ไม่เคยถูกส่งลงมา
  // แท็บรอบส่งเบิกจึงพิมพ์ค้นหาแล้วรายการไม่เปลี่ยนเลย รับเฉพาะคำที่ผู้ใช้ "กดค้นหา"
  // แล้วเท่านั้น (หน้าแม่ commit ให้) — ผูกกับ form control ตรง ๆ จะยิง request ทุกตัวอักษร
  @Input()
  set keywords(value: string | null | undefined) {
    const next = value?.trim() || null;
    if (next === this.keyword) {
      return;
    }

    this.keyword = next;
    this.loadRounds(1);
  }

  @Input()
  set departmentId(value: number | null | undefined) {
    const next = value ?? null;
    if (next === this.departmentIdValue) {
      return;
    }

    this.departmentIdValue = next;
    this.loadRounds(1);
  }

  rounds = signal<DisbursementRoundModel[]>([]);
  activeTab = signal<RoundQueueTab>('pending');
  listLoading = signal(false);
  actionLoading = signal(false);
  downloadingRoundId = signal<number | null>(null);
  page = signal(1);
  limit = signal(50);
  total = signal(0);
  totalPage = signal(1);

  actionRound = signal<DisbursementRoundModel | null>(null);
  actionKind = signal<RoundActionKind | null>(null);
  sendBackRound = signal<DisbursementRoundModel | null>(null);

  successTitle = signal('');
  successMessage = signal('');

  // One inline preview panel per round, keyed on the round id (the merged PDF is the
  // round's whole review artifact — ใบปะหน้า + every member's documents).
  previewPanels = signal<Partial<Record<number, PreviewPanelState>>>({});

  pageSizeOptions = [10, 20, 50];

  private termId: number | null = null;
  private departmentIdValue: number | null = null;
  private keyword: string | null = null;
  private readonly authService = inject(AuthService);
  private readonly roundService = inject(DisbursementRoundService);
  private readonly toast = inject(ToastService);
  private readonly sanitizer = inject(DomSanitizer);
  // Keeps raw URLs to revoke them; sanitized SafeResourceUrl lives in previewPanels.
  private readonly rawPreviewUrls = new Map<number, string>();

  ngOnDestroy(): void {
    this.stopRoundDocPolling();
    this.revokeAllPreviews();
  }

  reload(): void {
    this.loadRounds(this.page());
  }

  // --- Step filter ---

  // A getter, not a cached field: switching role must re-evaluate the tabs.
  get showTabs(): boolean {
    return this.pendingStatusForActiveRole() != null;
  }

  onTabChange(tab: RoundQueueTab): void {
    if (this.activeTab() === tab) {
      return;
    }

    this.activeTab.set(tab);
    this.loadRounds(1);
  }

  emptyLabel(): string {
    return this.activeTab() === 'pending' && this.showTabs
      ? 'ยังไม่มีรอบส่งเบิกที่ต้องดำเนินการ'
      : 'ยังไม่มีรอบส่งเบิก';
  }

  // --- Display helpers ---

  // A getter, not a cached field: switching role must re-render the badges.
  get statusAudience(): DisbursementStatusAudience {
    return disbursementStatusAudienceFor(this.authService.getActiveRoleId());
  }

  // ป้ายหลักสูตรอ่านจาก path ของรอบเอง ไม่ใช่ path_label ที่ API ส่งมา — คำเดิม
  // ("ภาคปกติ/ภาคนานาชาติ") ยังถูกใช้ในอีเมลและหน้าอื่น การแก้ที่ต้นทางจึงกระทบข้ามหน้า
  pathLabel(round: DisbursementRoundModel): string {
    return ROUND_PATH_LABELS[round.path] ?? round.path_label;
  }

  roundTitle(round: DisbursementRoundModel): string {
    const number = String(round.document_number ?? '').trim();
    return number !== '' ? `เลขที่ ${number}` : 'ยังไม่ระบุเลขที่เอกสาร';
  }

  roundNoLabel(round: DisbursementRoundModel): string {
    return round.round_no != null ? `รอบที่ ${round.round_no}` : '-';
  }

  departmentName(round: DisbursementRoundModel): string {
    const department = round.department;
    return department?.name_th ?? department?.name ?? department?.name_en ?? '-';
  }

  academicTermLabel(round: DisbursementRoundModel): string {
    const term = round.academic_term;
    if (term == null) {
      return '-';
    }

    return `${term.name}/${toBuddhistYear(term.year)}`;
  }

  memberCountLabel(round: DisbursementRoundModel): string {
    if (round.single_teacher_name != null && round.single_teacher_name !== '') {
      return round.single_teacher_name;
    }

    return `${round.member_count} รายการ`;
  }

  statusDisplay(round: DisbursementRoundModel): DisbursementStatusDisplay {
    return getDisbursementRoundStatusDisplay(round.status_code, this.statusAudience);
  }

  stationPersonNames(entry: DisbursementApprovalTrackEntry): string {
    return entry.person_names.length > 0 ? entry.person_names.join(', ') : 'ยังไม่ได้กำหนด';
  }

  isWindowClosed(round: DisbursementRoundModel): boolean {
    return round.window_closed;
  }

  isReturned(round: DisbursementRoundModel): boolean {
    return round.status_code === 'returned_to_officer';
  }

  formatAmount(value: number | null | undefined): string {
    return new Intl.NumberFormat('th-TH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value ?? 0);
  }

  formatDate(value?: string | null): string {
    if (value == null || value === '') {
      return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return [
      String(date.getDate()).padStart(2, '0'),
      String(date.getMonth() + 1).padStart(2, '0'),
      String(toBuddhistYear(date.getFullYear())),
    ].join('/');
  }

  // --- Round-merged inline preview (registry-detail's blob pattern) ---

  isPreviewExpanded(roundId: number): boolean {
    return this.previewPanels()[roundId] != null;
  }

  isPreviewLoading(roundId: number): boolean {
    return this.previewPanels()[roundId]?.loading ?? false;
  }

  previewHasError(roundId: number): boolean {
    return this.previewPanels()[roundId]?.error ?? false;
  }

  previewErrorMessage(roundId: number): string | null {
    return this.previewPanels()[roundId]?.errorMessage ?? null;
  }

  previewUrl(roundId: number): SafeResourceUrl | null {
    return this.previewPanels()[roundId]?.url ?? null;
  }

  onTogglePreview(round: DisbursementRoundModel): void {
    if (this.isPreviewExpanded(round.id)) {
      this.collapsePreview(round.id);
      return;
    }

    this.expandPreview(round);
  }

  // --- Downloads ---

  isDownloading(roundId: number): boolean {
    return this.downloadingRoundId() === roundId;
  }

  // TODO: ปุ่มดาวน์โหลดใบปะหน้าถูกซ่อนตามนโยบาย "โหลดทีเดียวตอน Export" — ยังไม่ตัดสินใจว่าจะลบ endpoint ทิ้งไหม
  onDownloadCoverMemo(round: DisbursementRoundModel): void {
    this.download(round, this.roundService.downloadCoverMemo(round.id), `cover_memo_${round.id}.pdf`);
  }

  // --- Primary action (sign / certify) ---

  canActPrimary(round: DisbursementRoundModel): boolean {
    return this.resolveActionKind(round) != null;
  }

  primaryActionLabel(round: DisbursementRoundModel): string {
    return this.actionLabelForKind(this.resolveActionKind(round));
  }

  onOpenPrimaryConfirm(round: DisbursementRoundModel): void {
    const kind = this.resolveActionKind(round);
    if (kind == null) {
      return;
    }

    this.actionRound.set(round);
    this.actionKind.set(kind);
    this.roundConfirmDialog.open();
  }

  onConfirmPrimaryAction(): void {
    const round = this.actionRound();
    const kind = this.actionKind();
    if (round == null || kind == null) {
      return;
    }

    this.actionLoading.set(true);
    this.resolveActionCall(kind, round.id)
      .pipe(finalize(() => this.actionLoading.set(false)))
      .subscribe({
        next: () => {
          this.roundConfirmDialog.close();
          this.collapsePreview(round.id);
          this.loadRounds(this.page());
          this.showSuccess(this.actionSuccessTitle(kind), this.actionSuccessMessage(kind));
        },
        error: (err) => {
          console.error(err);
          this.toast.toastError(this.extractErrorMessage(err));
        },
      });
  }

  primaryActionDialogTitle(): string {
    const kind = this.actionKind();
    const verb = this.actionLabelForKind(kind);
    return kind != null && ROUND_ACTION_VERB_STANDS_ALONE[kind]
      ? `ยืนยันการ${verb}`
      : `ยืนยันการ${verb}เอกสาร`;
  }

  primaryActionDialogMessage(): string {
    const kind = this.actionKind();
    const verb = this.actionLabelForKind(kind);
    const base = kind != null && ROUND_ACTION_VERB_STANDS_ALONE[kind]
      ? `ต้องการ${verb}ใช่หรือไม่ ?`
      : `ต้องการ${verb}เอกสารการเบิกจ่ายใช่หรือไม่ ?`;
    switch (kind) {
      case 'vice_dean_sign':
        return `${base} การอนุมัติจะปิดรอบและสร้างไฟล์สำหรับส่งเบิกทันที`;
      case 'finance_sign':
        return `${base} หลังจากนี้จะไม่สามารถส่งกลับแก้ไขได้`;
      case 'head_certify':
      case null:
      default:
        return base;
    }
  }

  primaryActionDialogConfirmText(): string {
    return this.actionLabelForKind(this.actionKind());
  }

  // --- Finance send-back (the single backward edge) ---

  canSendBack(round: DisbursementRoundModel): boolean {
    const roleId = this.authService.getActiveRoleId();

    return roleId === RoleID.FINANCE_OFFICER
      && round.status_code === 'pending_finance_sign'
      && !this.isWindowClosed(round);
  }

  onOpenSendBackDialog(round: DisbursementRoundModel): void {
    if (!this.canSendBack(round)) {
      return;
    }

    this.sendBackRound.set(round);
    this.sendBackDialog.open();
  }

  onSendBackCancelled(): void {
    this.sendBackRound.set(null);
  }

  onSendBackSubmitted(result: DisbursementRevisionDialogResult): void {
    const round = this.sendBackRound();
    if (round == null) {
      return;
    }

    this.actionLoading.set(true);
    this.roundService.sendBack(round.id, { comment: result.reason })
      .pipe(finalize(() => this.actionLoading.set(false)))
      .subscribe({
        next: () => {
          this.sendBackDialog.close();
          this.sendBackRound.set(null);
          this.collapsePreview(round.id);
          this.loadRounds(this.page());
          this.showSuccess('ส่งกลับแก้ไขสำเร็จ', 'ส่งกลับรอบส่งเบิกให้เจ้าหน้าที่สารบรรณเรียบร้อยแล้ว');
        },
        error: (err) => {
          console.error(err);
          this.toast.toastError(this.extractErrorMessage(err));
        },
      });
  }

  // --- Pagination ---

  onPageChange(page: number): void {
    if (page < 1 || page > this.totalPage() || page === this.page()) {
      return;
    }

    this.loadRounds(page);
  }

  onLimitChange(limit: number): void {
    if (!Number.isFinite(limit) || limit === this.limit()) {
      return;
    }

    this.limit.set(limit);
    this.loadRounds(1);
  }

  private showSuccess(title: string, message: string): void {
    this.successTitle.set(title);
    this.successMessage.set(message);
    this.actionSuccessDialog?.open();
  }

  private loadRounds(page: number): void {
    if (this.termId == null) {
      this.stopRoundDocPolling();
      this.rounds.set([]);
      this.total.set(0);
      this.totalPage.set(1);
      return;
    }

    this.revokeAllPreviews();
    this.listLoading.set(true);
    const params: DisbursementRoundListParams = {
      academic_term_id: this.termId,
      status: this.statusFilter(),
      keywords: this.keyword,
      page,
      limit: this.limit(),
    };
    if (this.departmentIdValue != null) {
      params.department_id = this.departmentIdValue;
    }

    this.roundService.getRounds(params).pipe(
      take(1),
      finalize(() => this.listLoading.set(false)),
    ).subscribe({
      next: (response) => {
        this.rounds.set(response.data);
        this.page.set(response.page);
        this.limit.set(response.limit);
        this.total.set(response.total);
        this.totalPage.set(Math.max(response.total_pages, 1));
        this.syncRoundDocPolling();
      },
      error: (err) => {
        console.error(err);
        this.rounds.set([]);
        this.page.set(page);
        this.total.set(0);
        this.totalPage.set(1);
      },
    });
  }

  // Same poller as the cover-memos page — ADR-CE, context/decisions/DECISION_DisbursementV20.md
  private syncRoundDocPolling(): void {
    if (!this.rounds().some((round) => roundArtifactsPending(round))) {
      this.stopRoundDocPolling();
      return;
    }
    if (this.stopRoundDocPoll != null) {
      return;
    }

    const subscription = timer(ROUND_DOC_POLL_INTERVAL_MS, ROUND_DOC_POLL_INTERVAL_MS)
      .subscribe(() => this.reloadRoundsQuietly());
    this.stopRoundDocPoll = () => {
      subscription.unsubscribe();
    };
  }

  private stopRoundDocPolling(): void {
    this.stopRoundDocPoll?.();
    this.stopRoundDocPoll = null;
  }

  // No listLoading, no preview teardown: a poll tick must not flicker the list or
  // collapse an open inline preview.
  private reloadRoundsQuietly(): void {
    if (this.termId == null) {
      this.stopRoundDocPolling();
      return;
    }

    this.roundService.getRounds({
      academic_term_id: this.termId,
      status: this.statusFilter(),
      page: this.page(),
      limit: this.limit(),
    }).pipe(take(1)).subscribe({
      next: (response) => {
        this.rounds.set(response.data);
        this.total.set(response.total);
        this.totalPage.set(Math.max(response.total_pages, 1));
        this.syncRoundDocPolling();
      },
      error: (err) => console.error(err),
    });
  }

  private pendingStatusForActiveRole(): DisbursementRoundStatusCode | null {
    const roleId = this.authService.getActiveRoleId();

    return roleId != null ? ROUND_PENDING_STATUS_BY_ROLE[roleId] ?? null : null;
  }

  private statusFilter(): DisbursementRoundStatusCode | null {
    return this.activeTab() === 'pending' ? this.pendingStatusForActiveRole() : null;
  }

  // การเงิน also sits in the phase-1 queue, so the round card must gate on the round's own
  // status — never on "this role can see rounds".
  private resolveActionKind(round: DisbursementRoundModel): RoundActionKind | null {
    if (this.isWindowClosed(round)) {
      return null;
    }

    const roleId = this.authService.getActiveRoleId();

    // admin ออกจาก DISBURSEMENT_ROUND_ROLE_IDS 2026-08-26 พร้อม acting_as_admin? — context/decisions/DECISION_RolesPermissions.md
    if (roleId === RoleID.FINANCE_OFFICER && round.status_code === 'pending_finance_sign') {
      return 'finance_sign';
    }

    if (roleId === RoleID.HEAD_OF_DEPARTMENT && round.status_code === 'pending_head') {
      return 'head_certify';
    }

    if (roleId === RoleID.VICE_DEAN_ADMINISTRATION && round.status_code === 'pending_vice_dean_admin') {
      return 'vice_dean_sign';
    }

    return null;
  }

  private resolveActionCall(kind: RoundActionKind, id: number): Observable<DisbursementRoundModel> {
    switch (kind) {
      case 'finance_sign':
        return this.roundService.financeSign(id);
      case 'head_certify':
        return this.roundService.headCertify(id);
      case 'vice_dean_sign':
        return this.roundService.viceDeanSign(id);
    }
  }

  private actionLabelForKind(kind: RoundActionKind | null): string {
    return kind != null ? ROUND_ACTION_VERB[kind] : '';
  }

  private actionSuccessTitle(kind: RoundActionKind): string {
    const verb = this.actionLabelForKind(kind);
    return ROUND_ACTION_VERB_STANDS_ALONE[kind] ? `${verb}สำเร็จ` : `${verb}เอกสารส่งเบิกสำเร็จ`;
  }

  private actionSuccessMessage(kind: RoundActionKind): string {
    const verb = this.actionLabelForKind(kind);
    const base = ROUND_ACTION_VERB_STANDS_ALONE[kind] ? `${verb}เรียบร้อยแล้ว` : `${verb}เอกสารส่งเบิกเรียบร้อยแล้ว`;
    switch (kind) {
      case 'vice_dean_sign':
        return `${base} ระบบกำลังสร้างไฟล์สำหรับส่งเบิก`;
      case 'head_certify':
      case 'finance_sign':
      default:
        return base;
    }
  }

  private expandPreview(round: DisbursementRoundModel): void {
    this.patchPreviewPanels(round.id, { loading: true, error: false, errorMessage: null, url: null });
    this.roundService.downloadMerged(round.id).pipe(take(1)).subscribe({
      next: (blob) => {
        const rawUrl = window.URL.createObjectURL(blob);
        this.rawPreviewUrls.set(round.id, rawUrl);
        this.patchPreviewPanels(round.id, {
          loading: false,
          error: false,
          errorMessage: null,
          url: this.sanitizer.bypassSecurityTrustResourceUrl(`${rawUrl}#toolbar=0&navpanes=0`),
        });
      },
      error: (err) => {
        console.error(err);
        void extractBlobErrorMessage(err, PREVIEW_ERROR_FALLBACK).then((errorMessage) => {
          this.patchPreviewPanels(round.id, { loading: false, error: true, errorMessage, url: null });
        });
      },
    });
  }

  private collapsePreview(roundId: number): void {
    this.revokePreview(roundId);
    this.previewPanels.update((panels) => {
      const next = { ...panels };
      delete next[roundId];
      return next;
    });
  }

  private patchPreviewPanels(roundId: number, state: PreviewPanelState): void {
    this.previewPanels.update((panels) => ({ ...panels, [roundId]: state }));
  }

  private revokePreview(roundId: number): void {
    const rawUrl = this.rawPreviewUrls.get(roundId);
    if (rawUrl != null) {
      window.URL.revokeObjectURL(rawUrl);
      this.rawPreviewUrls.delete(roundId);
    }
  }

  private revokeAllPreviews(): void {
    this.rawPreviewUrls.forEach((rawUrl) => window.URL.revokeObjectURL(rawUrl));
    this.rawPreviewUrls.clear();
    this.previewPanels.set({});
  }

  private download(round: DisbursementRoundModel, request: Observable<Blob>, fileName: string): void {
    if (this.downloadingRoundId() != null) {
      return;
    }

    this.downloadingRoundId.set(round.id);
    request.pipe(
      take(1),
      finalize(() => this.downloadingRoundId.set(null)),
    ).subscribe({
      next: (blob) => this.downloadBlob(blob, fileName),
      error: (err) => {
        console.error(err);
        this.toast.toastError(this.extractErrorMessage(err));
      },
    });
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  private extractErrorMessage(err: unknown): string {
    if (err !== null && typeof err === 'object' && 'error' in err) {
      const error = (err as { error?: unknown }).error;
      if (error !== null && typeof error === 'object' && 'errors' in error) {
        const errors = (error as { errors?: unknown }).errors;
        if (Array.isArray(errors) && errors.length > 0) {
          return String(errors[0]);
        }
        if (typeof errors === 'string') {
          return errors;
        }
      }
    }

    return 'ไม่สามารถดำเนินการได้';
  }
}
